import React from "react"
import styled from "styled-components"
import MaxWidthContainer from "@components/Layout/MaxWidthContainer"
import MaxWidthContent from "@components/Layout/MaxWidthContent"

const Container = styled.div`
  align-items: center;
  justify-content: center;
  display: block;
  width: 100%;
  margin: auto;
  text-align: center;

  iframe.zoho-web-form {
    box-sizing: border-box;
    display: block;
    width: 100%;
    max-width: 610px;
    margin: 0 auto;
    border: 0;
  }
`

interface CodeSnippetProps {
  code: string
}

const HUBSPOT_FORMS_SRC = "https://js.hsforms.net/forms/embed/v2.js"
const HUBSPOT_FORMS_SCRIPT_ID = "tmi-code-snippet-hubspot-forms"

let hubSpotFormsRequest: Promise<void> | null = null

const loadHubSpotForms = (src = HUBSPOT_FORMS_SRC): Promise<void> => {
  if ((window as any).hbspt) return Promise.resolve()
  if (hubSpotFormsRequest) return hubSpotFormsRequest

  hubSpotFormsRequest = new Promise<void>((resolve, reject) => {
    const existingScript = document.getElementById(
      HUBSPOT_FORMS_SCRIPT_ID
    ) as HTMLScriptElement | null

    const handleLoad = () => resolve()
    const handleError = () => {
      hubSpotFormsRequest = null
      reject(new Error("Unable to load the HubSpot forms script"))
    }

    if (existingScript) {
      existingScript.addEventListener("load", handleLoad, { once: true })
      existingScript.addEventListener("error", handleError, { once: true })
      return
    }

    const script = document.createElement("script")
    script.id = HUBSPOT_FORMS_SCRIPT_ID
    script.src = src
    script.async = true
    script.addEventListener("load", handleLoad, { once: true })
    script.addEventListener("error", handleError, { once: true })
    document.head.appendChild(script)
  })

  return hubSpotFormsRequest
}

const disableHubSpotFormCookies = (source: string): string => {
  if (
    !source.includes("hbspt.forms.create") ||
    source.includes("disableCookie")
  ) {
    return source
  }

  return source.replace(
    /hbspt\.forms\.create\s*\(\s*\{/g,
    (match) => `${match}\n disableCookie: true,`
  )
}

const isHubSpotFormsScript = (script: HTMLScriptElement): boolean => {
  const src = script.getAttribute("src")
  if (!src) return false

  try {
    const url = new URL(src, window.location.origin)
    return (
      (url.hostname === "hsforms.net" ||
        url.hostname.endsWith(".hsforms.net")) &&
      url.pathname === "/forms/embed/v2.js"
    )
  } catch {
    return false
  }
}

const CodeSnippet: React.FC<CodeSnippetProps> = ({ code }) => {
  const containerRef = React.useRef<HTMLDivElement>(null)

  const runScripts = async (scripts) => {
    for (const script of Array.from(scripts) as HTMLScriptElement[]) {
      if (isHubSpotFormsScript(script)) {
        await loadHubSpotForms(script.src)
        continue
      }

      // Preserve the previous behavior for non-HubSpot external scripts.
      if (script.getAttribute("src")) continue

      const src = disableHubSpotFormCookies(script.innerText)
      const fn = new Function("require", src)
      fn()
    }
  }

  React.useEffect(() => {
    const scripts =
      containerRef &&
      containerRef.current &&
      containerRef.current.getElementsByTagName("script")

    runScripts(scripts).catch((error) => {
      // A third-party embed should not crash the whole page.
      console.error("Unable to run embedded code snippet", error)
    })
  }, [])

  return (
    <MaxWidthContainer contentStyle={{ justifyContent: "center" }}>
      <MaxWidthContent cols={10}>
        <Container
          ref={containerRef}
          dangerouslySetInnerHTML={{ __html: code }}
        />
      </MaxWidthContent>
    </MaxWidthContainer>
  )
}

export default CodeSnippet
